import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import L from "leaflet";
import { CircleMarker, Popup, Tooltip, useMap } from "react-leaflet";
import { ChevronUp, Layers, Navigation, X } from "lucide-react";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Harita yakınlık katmanları — "bu nokta neyin yanında?" sorusunu cevaplayan
// ortak altyapı. Pasif Alanlar ve Protokollü Parklar haritaları aynı kontratı
// kullanır: bir "taban" katman (sekmenin kendi kayıtları) + üzerine açılıp
// kapanan referans katmanları (park / protokollü park / pasif alan).
// ---------------------------------------------------------------------------

/** Numeric alanlar backend'den string gelebilir; geçersizse null döner. */
export const toCoord = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/** İki koordinat arası kuş uçuşu mesafe (metre) — haversine, backend gerekmez. */
export const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
};

/** 340 → "340 m", 1240 → "1,2 km" */
export const formatDistance = (meters) => {
  if (!Number.isFinite(meters)) return "";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1).replace(".", ",")} km`;
};

/**
 * Google Maps yol tarifi bağlantısı. Koordinat varsa koordinatla açılır —
 * adres metni eksik/hatalı olsa bile doğru noktaya götürür; telefonda
 * doğrudan Google Maps uygulamasını açar.
 */
export const buildDirectionsUrl = (latitude, longitude, address) => {
  const lat = toCoord(latitude);
  const lng = toCoord(longitude);
  if (lat !== null && lng !== null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${address || ""}, Ankara`
  )}`;
};

/** Yeni sekmede aç (opener sızıntısı olmadan). */
export const openInNewTab = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Referans kaynakları. `key` aynı zamanda localStorage ve state anahtarıdır.
 * `path`/`params`: listeyi çeken uç. Parklar `summary=true` ile ağır alanlar
 * (boundaries vb.) olmadan gelir.
 */
export const NEARBY_SOURCES = {
  park: {
    key: "park",
    label: "Parklar",
    singular: "Park",
    color: "#16a34a",
    dot: "bg-green-600",
    accent: "accent-green-600",
    path: "/api/parks",
    params: { summary: true },
  },
  protokol: {
    key: "protokol",
    label: "Protokollü Parklar",
    singular: "Protokollü Park",
    // Indigo: sekmenin kimlik rengi (sınır poligonu, başlık, bağlantı çizgisi
    // hep bu ton) ve Leaflet'in varsayılan mavisinden ayrışır.
    color: "#4f46e5",
    dot: "bg-indigo-600",
    accent: "accent-indigo-600",
    path: "/api/protokol-parklar",
  },
  pasif: {
    key: "pasif",
    label: "Pasif Alanlar",
    singular: "Pasif Alan",
    color: "#f59e0b",
    dot: "bg-amber-500",
    accent: "accent-amber-600",
    path: "/api/passive-areas",
  },
};

/**
 * pinIcon — katman rengiyle aynı renkte damla pin üretir. Leaflet'in varsayılan
 * ikonu her zaman mavidir; taban katmanın lejant noktası ile haritadaki pin'in
 * rengi ayrışmasın diye kendi ikonumuzu çiziyoruz. Renk başına bir kez üretilir.
 */
const pinIconCache = {};
export const pinIcon = (color) => {
  if (pinIconCache[color]) return pinIconCache[color];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="-1 -1 28 40">
    <path d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 25 13 25s13-15.25 13-25C26 5.82 20.18 0 13 0z"
          fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
    <circle cx="13" cy="13" r="4.5" fill="#ffffff"/>
  </svg>`;
  pinIconCache[color] = L.divIcon({
    html: svg,
    className: "", // Leaflet'in varsayılan beyaz kutu/kenarlık stilini kaldırır
    iconSize: [28, 40],
    iconAnchor: [14, 39],
    popupAnchor: [0, -36],
    tooltipAnchor: [0, -32],
  });
  return pinIconCache[color];
};

const pickList = (data) =>
  Array.isArray(data) ? data : data?.parklar || data?.data || data?.items || data?.passive_areas || [];

/** Ham kayıtları ortak nokta şemasına çevirir; koordinatsızlar elenir. */
export const normalizePoints = (list, kind) => {
  const source = NEARBY_SOURCES[kind];
  const rows = [];
  (Array.isArray(list) ? list : []).forEach((item) => {
    const lat = toCoord(item.latitude);
    const lng = toCoord(item.longitude);
    if (lat === null || lng === null) return;
    rows.push({
      key: `${kind}:${item.id}`,
      kind,
      id: item.id,
      name: item.name,
      address: item.address,
      neighborhood: item.neighborhood || item.district,
      region_id: item.region_id,
      responsible:
        item.assigned_formen_name ||
        (Array.isArray(item.assigned_personnel_names) ? item.assigned_personnel_names.join(", ") : ""),
      lat,
      lng,
      color: source?.color,
      dot: source?.dot,
      typeLabel: source?.singular,
    });
  });
  return rows;
};

/**
 * useNearbyPoints — istenen kaynakları bir kez çeker (harita görünümü
 * açıldığında). Kart/tablo görünümünde hiç istek atılmaz.
 *
 * @param {string} apiBase       backend base URL
 * @param {string[]} sourceKeys  NEARBY_SOURCES anahtarları
 * @param {boolean} enabled      harita görünümü açık mı
 * @returns {{points: object, loading: boolean, loaded: boolean}}
 */
export function useNearbyPoints(apiBase, sourceKeys, enabled) {
  const [points, setPoints] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const keysSig = (sourceKeys || []).join(",");

  useEffect(() => {
    if (!enabled || loaded) return undefined;
    const keys = keysSig ? keysSig.split(",") : [];
    if (keys.length === 0) {
      setLoaded(true);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    Promise.allSettled(
      keys.map((key) =>
        axios.get(`${apiBase}${NEARBY_SOURCES[key].path}`, { params: NEARBY_SOURCES[key].params })
      )
    ).then((results) => {
      if (!alive) return;
      const next = {};
      results.forEach((res, index) => {
        const key = keys[index];
        next[key] = res.status === "fulfilled" ? normalizePoints(pickList(res.value.data), key) : [];
      });
      setPoints(next);
      setLoaded(true);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [apiBase, keysSig, enabled, loaded]);

  return { points, loading, loaded };
}

/**
 * useNearest — her kaynak nokta için en yakın N referansı hesaplar (kuş uçuşu).
 *
 * @param {Array} origins       [{ id, lat, lng }] — haritadaki kendi kayıtların
 * @param {Array} references    normalizePoints çıktılarının birleşimi
 * @param {number} limit
 * @returns {object} { [originId]: [{...ref, distance}] }
 */
export function useNearest(origins, references, limit = 3) {
  const originsSig = (origins || []).map((o) => `${o.id}:${o.lat},${o.lng}`).join("|");
  return useMemo(() => {
    const result = {};
    if (!references || references.length === 0) return result;
    (origins || []).forEach((origin) => {
      result[origin.id] = references
        .filter((ref) => !(ref.kind === origin.kind && ref.id === origin.id))
        .map((ref) => ({ ...ref, distance: haversineMeters(origin.lat, origin.lng, ref.lat, ref.lng) }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, limit);
    });
    return result;
    // `origins` her render'da yeniden üretiliyor; tetikleyici stabil imza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originsSig, references, limit]);
}

/**
 * usePersistedFlag — katman seçimini tarayıcıda kalıcı tutar; her girişte
 * kutuları tekrar işaretlemek gerekmez.
 */
export function usePersistedFlag(storageKey, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw === null ? initial : raw === "1";
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, value ? "1" : "0");
    } catch {
      /* private mode — yoksay */
    }
  }, [storageKey, value]);
  return [value, setValue];
}

/**
 * FitBoundsOnChange — `boundsKey` değişince haritayı verilen noktalara oturtur.
 * Filtre uygulandığında elle zoom'lama ihtiyacını ortadan kaldırır.
 */
export function FitBoundsOnChange({ bounds, boundsKey }) {
  const map = useMap();
  useEffect(() => {
    if (!Array.isArray(bounds) || bounds.length === 0) return;
    if (bounds.length === 1) {
      map.setView(bounds[0], 16);
      return;
    }
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 16 });
    // bounds her render'da yeniden üretiliyor; tetikleyici stabil `boundsKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, map]);
  return null;
}

/**
 * NearbyLayerPanel — haritanın köşesine yapışan katman kutusu.
 * MapContainer'ın KARDEŞİ olarak render edilmeli; kapsayıcı `relative` olmalı.
 *
 * Taban katman da (sekmenin kendi kayıtları) kapatılabilir: sadece çevredeki
 * parklara bakmak istendiğinde kutucuğu boşaltmak yeter.
 *
 * @param {{label, dot, accent, count, checked, onChange}} base  taban katman
 * @param {Array} layers    [{ key, label, dot, accent, count, checked, onChange }]
 * @param {boolean} loading
 * @param {boolean} showClear
 * @param {function} onClearLink
 */
export function NearbyLayerPanel({ base, layers = [], loading = false, showClear = false, onClearLink }) {
  // Katlanmış durum üç harita için ortak ve kalıcı — bir kez toplayan kullanıcı
  // için hep toplu kalır, haritadan yer çalmaz.
  const [collapsed, setCollapsed] = usePersistedFlag("mapLayerPanel.collapsed", false);
  const rows = base ? [{ key: "__base__", ...base }, ...layers] : layers;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        title="Katmanlar"
        className="absolute top-12 right-2 z-[1000] flex items-center gap-1 rounded-md border border-gray-200 bg-white/90 px-1.5 py-1 shadow backdrop-blur hover:bg-white"
      >
        <Layers className="h-3.5 w-3.5 text-gray-500" />
        {rows
          .filter((layer) => layer.checked)
          .map((layer) => (
            <span key={layer.key} className={`h-2 w-2 rounded-full ${layer.dot}`} />
          ))}
      </button>
    );
  }

  return (
    <div className="absolute top-12 right-2 z-[1000] w-[168px] space-y-1 rounded-md border border-gray-200 bg-white/90 p-1.5 text-[11px] leading-tight shadow backdrop-blur">
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        title="Paneli topla"
        className="flex w-full items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
      >
        <Layers className="h-3 w-3 text-gray-500" />
        <span>Katmanlar</span>
        <ChevronUp className="ml-auto h-3 w-3 text-gray-400" />
      </button>

      {rows.map((layer) => (
        <label key={layer.key} className="flex cursor-pointer items-center gap-1 py-px">
          <input
            type="checkbox"
            className={`h-3 w-3 shrink-0 ${layer.accent}`}
            checked={layer.checked}
            onChange={(e) => layer.onChange(e.target.checked)}
          />
          <span className={`h-2 w-2 shrink-0 rounded-full ${layer.dot}`} />
          <span className="truncate text-gray-700">{layer.label}</span>
          <span className="ml-auto shrink-0 text-gray-400">{layer.count}</span>
        </label>
      ))}

      {loading && <p className="text-gray-400">Yükleniyor…</p>}

      {showClear && (
        <button
          type="button"
          onClick={onClearLink}
          className="flex w-full items-center justify-center gap-1 rounded border border-gray-300 bg-gray-50 px-1 py-0.5 text-gray-700 hover:bg-gray-100"
        >
          <X className="h-3 w-3" />
          Bağlantıyı temizle
        </button>
      )}
    </div>
  );
}

/**
 * NearbyPointMarkers — referans noktalarını küçük daireler olarak çizer.
 * Damla-pin yerine daire: sekmenin kendi pin'lerinin görsel baskınlığı bozulmaz.
 *
 * @param {Array} points            görünür referanslar
 * @param {function} [regionNameOf] region_id → bölge adı
 */
export function NearbyPointMarkers({ points = [], regionNameOf }) {
  return points.map((ref) => (
    <CircleMarker
      key={ref.key}
      center={[ref.lat, ref.lng]}
      radius={6}
      pathOptions={{ color: ref.color, fillColor: ref.color, fillOpacity: 0.75, weight: 1.5 }}
    >
      <Tooltip direction="top" offset={[0, -6]}>
        <span className="font-medium">{ref.name}</span>
      </Tooltip>
      <Popup>
        <div className="min-w-[210px] max-w-[280px] space-y-1.5">
          <h3 className="border-b pb-1 text-sm font-bold" style={{ color: ref.color }}>
            {ref.name}
          </h3>
          <p className="text-xs text-gray-500">{ref.typeLabel}</p>
          {ref.neighborhood && (
            <p className="text-sm">
              <span className="font-medium text-gray-600">Mahalle:</span> {ref.neighborhood}
            </p>
          )}
          {ref.region_id && regionNameOf && (
            <p className="text-sm">
              <span className="font-medium text-gray-600">Bölge:</span> {regionNameOf(ref.region_id)}
            </p>
          )}
          {ref.responsible && (
            <p className="text-sm">
              <span className="font-medium text-gray-600">Görevli:</span> {ref.responsible}
            </p>
          )}
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => openInNewTab(buildDirectionsUrl(ref.lat, ref.lng, ref.address))}
          >
            <Navigation className="mr-1 h-3 w-3" />
            Yol Tarifi
          </Button>
        </div>
      </Popup>
    </CircleMarker>
  ));
}

/**
 * NearestPlaces — popup içindeki "en yakın N" listesi. Satıra tıklayınca
 * `onSelect(ref)` çağrılır (katmanı aç + bağlantı çizgisini kur).
 */
export function NearestPlaces({ nearest = [], onSelect, title = "En yakın", loading = false }) {
  if (nearest.length === 0) {
    return loading ? (
      <p className="border-t pt-1.5 text-[11px] text-gray-400">Yakındaki kayıtlar yükleniyor…</p>
    ) : null;
  }
  return (
    <div className="border-t pt-1.5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      <div className="space-y-0.5">
        {nearest.map((ref) => (
          <button
            key={ref.key}
            type="button"
            onClick={() => onSelect?.(ref)}
            className="flex w-full items-center justify-between gap-2 rounded px-1 py-0.5 text-left text-xs hover:bg-gray-100"
            title="Haritada göster"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span className={`h-2 w-2 shrink-0 rounded-full ${ref.dot}`} />
              <span className="truncate">{ref.name}</span>
            </span>
            <span className="shrink-0 text-gray-500">{formatDistance(ref.distance)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default NearbyLayerPanel;
